import type { Variants } from "motion/react";

export const RADIAL_MENU_OPTION_SIZE = 48;

/**
 * Calculate the starting position for the rotational swing animation.
 * Options start from below (rotated +90 degrees from their final position)
 * at a position between center and end, then swing up into place.
 */
const getStartPosition = (finalX: number, finalY: number) => {
  // Calculate the radius (distance from center)
  const radius = Math.sqrt(finalX * finalX + finalY * finalY);

  // Calculate the final angle
  const finalAngle = Math.atan2(finalY, finalX);

  // Start position is rotated +90 degrees (coming from below)
  const startAngle = finalAngle + Math.PI / 2;

  // Start at 50% of the radius (between center and end position)
  const startRadius = radius * 0.5;

  return {
    x: Math.cos(startAngle) * startRadius,
    y: Math.sin(startAngle) * startRadius,
  };
};

/**
 * Animation variants for the radial menu options with rotational swing.
 * Options swing in from below in a curved arc motion.
 * Uses tween with easeOut for smooth, controlled motion.
 */
export const optionVariants: Variants = {
  closed: (custom: { x: number; y: number }) => {
    const startPos = getStartPosition(custom.x, custom.y);
    return {
      x: startPos.x,
      y: startPos.y,
      scale: 0.6,
      opacity: 0,
      transition: {
        duration: 0.25,
        ease: "easeIn",
      },
    };
  },
  open: (custom: { x: number; y: number }) => ({
    x: custom.x,
    y: custom.y,
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.35,
      ease: [0.25, 0.1, 0.25, 1],
    },
  }),
};

/**
 * Animation variants for the option button expanding to show label.
 * Width animation - expands horizontally.
 */
export const optionButtonVariants: Variants = {
  collapsed: {
    width: RADIAL_MENU_OPTION_SIZE,
    height: RADIAL_MENU_OPTION_SIZE,
    transition: {
      duration: 0.15,
      ease: "easeInOut",
    },
  },
  expanded: {
    width: "auto",
    height: "auto",
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};
